import { observer } from "mobx-react-lite";
import store, { gameStore } from "@/store";
import { Routes, Route, useNavigate } from "react-router-dom";
import { isDevelopment, isProduction, isTelegram, isWeb } from "@/utils";
import clsx from "clsx";
import "@mysten/dapp-kit/dist/index.css";
import "swiper/css/pagination";
import "swiper/css";
import Loader from "@/layout/Loader";
import LockScreen from "./layout/LockScreen";
import ErrorHint from "@/components/ErrorHint";
import DevOverlay from "./layout/DevOverlay";
import TelegramHeaderManager from "./components/TelegramHeaderManager";
import ToastContainer from "./components/ToastContainer/ToastContainer";
import CapybaraLayout from "./layout/CapybaraLayout";
import { menuItems } from "./constants";
import { LeagueLevelUpModal } from "./layout/LevelUpModal";
import Story from "./layout/Story";
import WalletLayout from "./layout/WalletLayout";
import WalletAuthLayout from "./layout/WalletAuthLayout";
import { AuthWalletsFlow } from "./components/AuthWallets";
import { StashedAuth } from "./components/CreateWallet/components/StashedAuth";
import WalletTransfer from "./views/WalletTransfer";
import { StashedTransfer } from "./components/CreateWallet/components/StashedTransfer";
import { AutoFeedBotEarnedModal } from "./views/Boost/Modals/AutoFeedBotEarned";
import WalletBuy from "./views/WalletBuy";
import WalletPage from "./views/WalletPage";
import { Collections } from "./views/Collections";
import { BuyMoneyBag } from "./views/BuyMoneyBag";
import { EnokiProvider } from "./components/EnokiProvider";
import { StashedProvider } from "./components/StashedProvider";
import { Explore } from "./views/Earn/Explore";
import { StashedNftMint } from "./components/CreateWallet/components/StashedNftMint";
import { StashedNftUpdate } from "./components/CreateWallet/components/StashedNftUpdate";
import SelectWallet from "./views/SelectWallet";
import { MintingNftOKX } from "./components/MintingNftOKX";
import { MintingNft } from "./components/MintingNft";
import { Auth } from "./components/CreateWallet/components/Auth";
import { StashedMintingMoneyBag } from "./components/CreateWallet/components/StashedMintingMoneyBag";
import { StashedOpeningMoneyBag } from "./components/CreateWallet/components/StashedOpeningMoneyBag";
import { SuiClientProvider } from "@mysten/dapp-kit";
import { networkConfig } from "./components/StashedProvider/networkConfig";
import { Login } from "./views/Login/Login";
import { useEffect } from "react";
import { backend } from "./services";
import { useSearchParams } from "./hooks/useSearchParams";

const App = observer(() => {
  const className = clsx("appLayout", { ["isDevelopment"]: isDevelopment });
  const navigate = useNavigate();
  const [{ token: queryToken }, searchParams ] = useSearchParams<{ token: string }>();

  const updateToken = async (token: string) => {
    try {
      store.setLoading(true);
      backend.setLoginToken(token);
      const response = await backend.refreshToken();
      store.setUserData(response.user || {});
    } catch (error) {
      store.setLoading(false);
      const query = searchParams.toString();
      navigate('/login' + (query ? `?${query}` : ''));
      throw error;
    }
  }

  const initWebApp = async () => {
    if (!isWeb) return;

    if (isDevelopment) {
      backend.setDevToken();
      return store.init();
    }

    const token = localStorage.getItem("token") ?? queryToken;
    await updateToken(token);
    store.init();
  }

  useEffect(() => {
    initWebApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (window.location.pathname === "/auth") {
    return (
      <WalletAuthLayout>
        <StashedProvider>
          <EnokiProvider>
            <Auth />
          </EnokiProvider>
        </StashedProvider>
      </WalletAuthLayout>
    );
  }

  // if (window.location.pathname === "/transferOKX") {
  //   return (
  //     <WalletAuthLayout>
  //       <StashedProvider>
  //         <EnokiProvider>
  //           <StashedAuthAndTransferOKX />
  //         </EnokiProvider>
  //       </StashedProvider>
  //     </WalletAuthLayout>
  //   );
  // }

  if (window.location.pathname === "/authWallets") {
    return (
      <WalletAuthLayout>
        <EnokiProvider>
          <AuthWalletsFlow />
        </EnokiProvider>
      </WalletAuthLayout>
    );
  }

  if (window.location.pathname === "/authStashed") {
    return (
      <WalletAuthLayout>
        <StashedProvider>
          <StashedAuth />
        </StashedProvider>
      </WalletAuthLayout>
    );
  }

  if (window.location.pathname === "/transferStashed") {
    console.log("transferStashed");
    return (
      <WalletAuthLayout>
        <StashedProvider>
          <StashedTransfer />
        </StashedProvider>
      </WalletAuthLayout>
    );
  }

  if (window.location.pathname === "/MintMoneyBag") {
    return (
      <WalletAuthLayout>
        <StashedProvider>
          <StashedMintingMoneyBag />
        </StashedProvider>
      </WalletAuthLayout>
    );
  }

  if (window.location.pathname === "/OpenMoneyBag") {
    return (
      <WalletAuthLayout>
        <StashedProvider>
          <StashedOpeningMoneyBag />
        </StashedProvider>
      </WalletAuthLayout>
    );
  }

  if (window.location.pathname === "/StashedNftMint") {
    return (
      <WalletAuthLayout>
        <StashedProvider>
          <StashedNftMint />
        </StashedProvider>
      </WalletAuthLayout>
    );
  }

  if (window.location.pathname === "/StashedNftUpdate") {
    return (
      <WalletAuthLayout>
        <StashedProvider>
          <StashedNftUpdate />
        </StashedProvider>
      </WalletAuthLayout>
    );
  }

  if (window.location.pathname === "/nftMint") {
    return (
      <WalletAuthLayout>
        <StashedProvider>
          <MintingNft />
        </StashedProvider>
      </WalletAuthLayout>
    );
  }

  if (window.location.pathname === "/login") {
    return <div className={className}><Login /></div>
  }

  return (
    <div className={className}>
      {isTelegram && <TelegramHeaderManager />}
      {store.error && <ErrorHint />}
      {store.qr && <LockScreen />}
      {store.loading && !store.qr && <Loader />}
      {/* {!rootStore.isUserInWhiteList && <ImproveScreen />} */}
      {store.gameReady && (
        <>
          {!isProduction && gameStore.devOverlay && <DevOverlay />}
          <ToastContainer />
          <Story />
          <LeagueLevelUpModal />
          <AutoFeedBotEarnedModal />
          <Routes>
            <Route
              key={"wallet"}
              path="/wallet"
              element={
                <StashedProvider>
                  <WalletLayout>
                    <WalletPage />
                  </WalletLayout>
                </StashedProvider>
              }
            />
            <Route
              key={"WalletTransfer"}
              path="/WalletTransfer"
              element={
                <StashedProvider>
                  <WalletLayout>
                    <WalletTransfer />
                  </WalletLayout>
                </StashedProvider>
              }
            />
            <Route
              key={"WalletBuy"}
              path="/WalletBuy"
              element={
                <StashedProvider>
                  <WalletLayout>
                    <WalletBuy />
                  </WalletLayout>
                </StashedProvider>
              }
            />

            <Route
              key={"Explore"}
              path="/explore/:id"
              element={
                <CapybaraLayout isHideMenu={false}>
                  <Explore />
                </CapybaraLayout>
              }
            />

            <Route
              key={"Collections"}
              path="/collections"
              element={
                <CapybaraLayout>
                  <Collections />
                </CapybaraLayout>
              }
            />

            <Route
              key={"BuyMoneyBag"}
              path="/buyMoneyBag"
              element={
                <SuiClientProvider
                  networks={networkConfig}
                  defaultNetwork="mainnet"
                >
                  <BuyMoneyBag />
                </SuiClientProvider>
              }
            />

            <Route
              key={"BuyMoneyBag"}
              path="/selectWallet"
              element={<SelectWallet />}
            />

            <Route
              key={"MintingNftOKX"}
              path="/MintingNftOKX"
              element={
                <CapybaraLayout isHideMenu={true}>
                  <MintingNftOKX />
                </CapybaraLayout>
              }
            />

            {menuItems.map((item) => (
              <Route
                key={item.path}
                path={item.path}
                element={
                  <CapybaraLayout>
                    <item.component />
                  </CapybaraLayout>
                }
              />
            ))}
          </Routes>
        </>
      )}
    </div>
  );
});

export default App;
